import cookie from "react-cookies";
import constants from "../Constants";

const baseURL = constants.baseURL;

const authHeaders = (includeAuth = true) => {
  const headers = { "Content-Type": "application/json" };
  if (includeAuth) {
    const token = cookie.load("candidateKey");
    if (token) headers.Authorization = token;
  }
  return headers;
};

async function creatorFetch(path, options = {}) {
  const { auth = true, headers: extraHeaders, ...fetchOptions } = options;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 30000);
  try {
    const res = await fetch(`${baseURL}${path}`, {
      ...fetchOptions,
      signal: controller.signal,
      headers: { ...authHeaders(auth), ...extraHeaders },
    });
    const data = await res.json();
    if (!res.ok) {
      const error = new Error(data.error || "Request failed");
      error.status = res.status;
      error.code = data.code;
      error.missing_fields = data.missing_fields;
      error.profile_path = data.profile_path;
      error.license_path = data.license_path;
      error.license_version = data.license_version;
      error.intro_path = data.intro_path;
      error.intro_status = data.intro_status;
      error.script_id = data.script_id;
      error.block_note = data.block_note;
      error.field_errors = data.field_errors;
      throw error;
    }
    return data;
  } finally {
    clearTimeout(timeout);
  }
}

export const listCreatorScripts = () => creatorFetch("/creator/scripts");
export const getCreatorScript = (id) => creatorFetch(`/creator/scripts/${id}`);
export const startCreatorScript = (id) =>
  creatorFetch(`/creator/scripts/${id}/start`, { method: "POST", body: "{}" });

export const listMyCreatorSubmissions = () => creatorFetch("/creator/me/submissions");
export const getMyCreatorSubmission = (id) =>
  creatorFetch(`/creator/me/submissions/${id}`);

export const requestClipUploadUrl = (submissionId, shot, fileMeta) =>
  creatorFetch(`/creator/me/submissions/${submissionId}/clips/${shot}/upload-url`, {
    method: "POST",
    body: JSON.stringify({ file: fileMeta }),
  });

export const completeClipUpload = (submissionId, shot, body) =>
  creatorFetch(`/creator/me/submissions/${submissionId}/clips/${shot}/complete`, {
    method: "POST",
    body: JSON.stringify(body),
  });

export const requestDeliverableUploadUrl = (submissionId, deliverable, fileMeta) =>
  creatorFetch(
    `/creator/me/submissions/${submissionId}/deliverables/${deliverable}/upload-url`,
    {
      method: "POST",
      body: JSON.stringify({ file: fileMeta }),
    }
  );

export const completeDeliverableUpload = (submissionId, deliverable, body) =>
  creatorFetch(
    `/creator/me/submissions/${submissionId}/deliverables/${deliverable}/complete`,
    {
      method: "POST",
      body: JSON.stringify(body),
    }
  );

export const submitCreatorSubmission = (id) =>
  creatorFetch(`/creator/me/submissions/${id}/submit`, { method: "POST", body: "{}" });

export const getCreatorBalance = () => creatorFetch("/creator/me/balance");
export const getCreatorPayouts = () => creatorFetch("/creator/me/payouts");
export const getCreatorBanking = () => creatorFetch("/creator/me/banking");
export const saveCreatorBanking = (body) =>
  creatorFetch("/creator/me/banking", { method: "POST", body: JSON.stringify(body) });

export const getCreatorLicense = () =>
  creatorFetch("/creator/license", { auth: true });

export const getCreatorPrivacyNotice = () =>
  creatorFetch("/creator/privacy", { auth: false });

export const acceptCreatorLicense = (version) =>
  creatorFetch("/creator/me/license/accept", {
    method: "POST",
    body: JSON.stringify({ version }),
  });

export const getMyCreatorIntro = () => creatorFetch("/creator/me/intro");

export const requestIntroUploadUrl = (fileMeta) =>
  creatorFetch("/creator/me/intro/upload-url", {
    method: "POST",
    body: JSON.stringify({ file: fileMeta }),
  });

export const completeIntroUpload = (body) =>
  creatorFetch("/creator/me/intro/complete", {
    method: "POST",
    body: JSON.stringify(body),
  });

export const submitCreatorIntro = () =>
  creatorFetch("/creator/me/intro/submit", { method: "POST", body: "{}" });

/**
 * PUT to S3 with upload progress. Returns a promise + cancel().
 */
export function putFileWithProgress(uploadUrl, file, { onProgress, contentType } = {}) {
  const xhr = new XMLHttpRequest();
  const promise = new Promise((resolve, reject) => {
    xhr.open("PUT", uploadUrl);
    xhr.setRequestHeader("Content-Type", contentType || file.type || "video/mp4");
    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable || !onProgress) return;
      onProgress(Math.round((event.loaded / event.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error("Upload failed. Check your connection and try again."));
    xhr.onabort = () => reject(new Error("Upload cancelled"));
    xhr.send(file);
  });
  return {
    promise,
    cancel: () => xhr.abort(),
  };
}
